import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { pollJDGenerationStatus, triggerSemanticMatching, getJdMatchingStatus } from "../../../../api/client";
import { Copy, Check, Users, Plus, Filter, Eye, X, ChevronDown, Edit, Trash2, Save, Upload, ArchiveRestore, Clock, Mic, Loader2, ClipboardCheck, Search, Building2, Calendar, ArrowUpDown, CheckCircle2, FileText, MoreVertical, Layers } from "lucide-react";

import { CircularProgress, Collapse, Menu, MenuItem, Drawer, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import PremiumSelect from "../../component/PremiumSelect";
import PremiumDatePicker from "../../component/PremiumDatePicker";
import AddDepartmentModal from "../../component/AddDepartmentModal";
import AddJobDescriptionDialog from "../../component/AddJobDescriptionDialog";
import AIQuestionGeneratorModal from "../../component/AIQuestionGeneratorModal";
import QuestionBankPickerModal from "../../component/QuestionBankPickerModal";
import NotificationPopup from "../../component/NotificationPopup";
import ShowDescriptionDialog from "../../component/ShowDescriptionDialog";
import Pagination from "../../component/Pagination";
import ResumeMatchingModal from "./Resumematchingmodal";
import Timeline from "../../component/Timeline";
import JDDraftsModal from "./JDDraftsModal";
import JDFromAudioModal from "./JDFromAudioModal";
import BenchmarksPanel from "./BenchmarksPanel";
import { saveJdBenchmarks, splitJdFormAndBenchmarks, toBenchmarkPayload } from "./benchmarkLabels";
import { convertMarkdownToHtml } from "../../../../utils/markdownToHTML";
import parse from "html-react-parser";
import { IoArchiveOutline } from "react-icons/io5";
import { calculateInterviewDurationRequirements, formatDurationBreakdownText } from "../../../../utils/durationCalculator";
import "../../admin.css";
import { usePageLoading } from "../../../../contexts/LoadingContext";
import PageHeader, { PageCard, PageHeaderButton, PageShell } from "../../../common/PageHeader";

const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatFullDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const getQuestionTypeOptions = (t) => [
  { value: "concept", label: t('jobDescription.conceptual') },
  { value: "coding", label: t('jobDescription.coding') },
  { value: "mcq", label: t('jobDescription.multipleChoice') },
  { value: "behavioral", label: t('jobDescription.behavioral') },
  { value: "general", label: t('jobDescription.general') },
];

const getQuestionTypeColor = (type) => {
  const colors = {
    concept: "bg-purple-50 text-purple-700 border border-purple-200",
    coding: "bg-orange-50 text-orange-700 border border-orange-200",
    mcq: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    behavioral: "bg-pink-50 text-pink-700 border border-pink-200",
    general: "bg-gray-50 text-gray-700 border border-gray-200",
  };
  return colors[type] || colors.general;
};

const isJdBusy = (jd) => {
  const match = jd.matching_status?.toLowerCase();
  const sched = jd.scheduling_status?.toLowerCase();
  return match === 'processing' || sched === 'processing' || sched === 'scheduling';
};

const getStatusBadge = (status, t) => {
  if (!status) return <span className="text-gray-400">—</span>;

  const statusLower = status.toLowerCase();
  let colorClass = "bg-gray-100 text-gray-800";
  let label = status;

  if (statusLower === 'processing' || statusLower === 'scheduling') {
    colorClass = "bg-blue-100 text-blue-800 animate-pulse";
    label = t(`jobDescription.status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`);
  } else if (statusLower === 'ready' || statusLower === 'completed') {
    colorClass = "bg-green-100 text-green-800";
    label = t(`jobDescription.status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`);
  } else if (statusLower === 'failed') {
    colorClass = "bg-red-100 text-red-800";
    label = t('jobDescription.statusFailed');
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
};

const hoverTipSlotProps = {
  popper: {
    modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
  },
  tooltip: {
    sx: {
      background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
      color: '#fff',
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.45,
      letterSpacing: '0.01em',
      borderRadius: '8px',
      px: 1.5,
      py: 0.75,
      boxShadow: '0 8px 20px rgba(14, 165, 233, 0.28)',
      maxWidth: 360,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
};

const OverflowTooltip = ({ text, className }) => {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const update = () => {
      setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
    };

    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(el);
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [text]);

  if (!text) return null;

  return (
    <Tooltip
      title={isOverflowing ? text : ''}
      placement="top"
      enterDelay={200}
      disableInteractive
      arrow
      slotProps={hoverTipSlotProps}
    >
      <span ref={ref} className={className}>
        {text}
      </span>
    </Tooltip>
  );
};

const JobDescriptionCard = ({ jobDescription, onAddQuestion, onUpdateQuestions, onGenerateAiQuestion, onAddFromQuestionBank }) => {
  const { t } = useTranslation();
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState("");
  const [editedType, setEditedType] = useState("concept");
  const [editedTimeLimit, setEditedTimeLimit] = useState(5);
  const [editedMcqOptions, setEditedMcqOptions] = useState(["", "", "", ""]);
  const [editedMcqCorrectIndex, setEditedMcqCorrectIndex] = useState(0);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const questionTypeOptions = getQuestionTypeOptions(t);

  const attachedProfileName =
    jobDescription?.attached_profiles?.[0]?.name ||
    jobDescription?.questions?.find((q) => typeof q === "object" && (q.profileName || q.profile_name))?.profileName ||
    jobDescription?.questions?.find((q) => typeof q === "object" && (q.profileName || q.profile_name))?.profile_name;

  const handleEdit = (index, question) => {
    setEditingIndex(index);
    setEditedQuestion(question?.nameOfQuestion || question);
    setEditedType(question?.type || "concept");
    setEditedTimeLimit(question?.time_limit || 5);
    if (question?.type === "mcq" && question.choices) {
      setEditedMcqOptions(question.choices.map((c) => c.text || ""));
      const cIdx = question.choices.findIndex((c) => c.is_correct);
      setEditedMcqCorrectIndex(cIdx >= 0 ? cIdx : 0);
    } else {
      setEditedMcqOptions(["", "", "", ""]);
      setEditedMcqCorrectIndex(0);
    }
  };

  const handleSave = async (index) => {
    if (!editedQuestion.trim()) return;
    setIsUpdating(true);
    try {
      const updatedQuestions = [...(jobDescription.questions || [])];
      const existing = updatedQuestions[index];
      updatedQuestions[index] = typeof existing === "object" && existing.nameOfQuestion
        ? {
          ...existing,
          nameOfQuestion: editedQuestion.trim(),
          type: editedType,
          ...(editedType === "coding" && { time_limit: editedTimeLimit > 0 ? editedTimeLimit : 5 }),
          ...(editedType === "mcq" && {
            choices: editedMcqOptions.map((text, i) => ({
              text: text || "",
              is_correct: i === editedMcqCorrectIndex,
            })),
          }),
        }
        : {
          nameOfQuestion: editedQuestion.trim(),
          type: editedType,
          ...(editedType === "coding" && { time_limit: editedTimeLimit > 0 ? editedTimeLimit : 5 }),
          ...(editedType === "mcq" && {
            choices: editedMcqOptions.map((text, i) => ({
              text: text || "",
              is_correct: i === editedMcqCorrectIndex,
            })),
          }),
        };

      await api.put(`job-descriptions/${jobDescription.id}/`, { questions: updatedQuestions });
      onUpdateQuestions(jobDescription.id, updatedQuestions);
      setEditingIndex(null);
      setEditedQuestion("");
      setEditedType("concept");
      setEditedTimeLimit(5);
      setEditedMcqOptions(["", "", "", ""]);
      setEditedMcqCorrectIndex(0);
    } catch (error) {
      alert(t('jobDescription.failedToUpdateQuestion'));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (deleteIndex === null) return;
    setIsUpdating(true);
    try {
      const updatedQuestions = [...(jobDescription.questions || [])];
      updatedQuestions.splice(deleteIndex, 1);
      await api.put(`job-descriptions/${jobDescription.id}/`, { questions: updatedQuestions });
      onUpdateQuestions(jobDescription.id, updatedQuestions);
      setDeleteIndex(null);
    } catch (error) {
      alert(t('jobDescription.failedToDeleteQuestion'));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50/50 via-indigo-50/30 to-purple-50/20 border border-blue-200/80 rounded-xl p-3.5 sm:p-4 my-2.5 mx-3 shadow-2xs max-w-full min-w-0">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2.5 pb-2.5 border-b border-blue-200/60">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-bold text-gray-900 truncate">
              {t('jobDescription.questionsFor')} <span className="text-blue-600 font-semibold">{jobDescription.title}</span>
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onAddFromQuestionBank(jobDescription)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 hover:bg-blue-50 text-blue-700 rounded-lg transition-all shadow-2xs text-xs font-semibold cursor-pointer disabled:opacity-50"
              disabled={isUpdating}
            >
              <LibraryBooksOutlinedIcon sx={{ fontSize: 14 }} /> {t('questionBank.addFromBank', 'Add from Question Bank')}
            </button>
            <button
              onClick={() => onGenerateAiQuestion(jobDescription)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all shadow-xs text-xs font-semibold cursor-pointer disabled:opacity-50"
              disabled={isUpdating}
            >
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 14 }} /> {t('jobDescription.generateWithAI')}
            </button>
            <button
              onClick={() => onAddQuestion(jobDescription.id)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shadow-xs text-xs font-semibold cursor-pointer disabled:opacity-50"
              disabled={isUpdating}
            >
              <Plus size={14} className="stroke-[2.5]" /> {t('jobDescription.addQuestion')}
            </button>
            {attachedProfileName && (
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold text-xs shadow-2xs whitespace-nowrap"
                title={`Attached Profile: ${attachedProfileName}`}
              >
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="font-bold max-w-[160px] truncate">{attachedProfileName}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 sm:p-3.5 border border-gray-200 shadow-2xs">
          <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">{t('jobDescription.interviewQuestions')}</span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200/60 shadow-2xs">
              {jobDescription.questions?.length || 0}
            </span>
          </div>
          {(() => {
            const durationInfo = calculateInterviewDurationRequirements(jobDescription?.questions || []);
            if (durationInfo.total_questions === 0) return null;
            return (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-3 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-blue-900 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Auto-Calculated Interview Duration
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className="bg-white border border-blue-200 text-blue-800 px-2 py-0.5 rounded font-semibold text-[11px]">
                      Min: {durationInfo.minimum_duration_minutes} min
                    </span>
                    <span className="bg-blue-600 text-white px-2 py-0.5 rounded font-bold text-[11px]">
                      Recommended: {durationInfo.recommended_duration_minutes} min
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-[11px] leading-relaxed">
                  {formatDurationBreakdownText(durationInfo.breakdown, durationInfo.total_questions, durationInfo.minimum_duration_minutes)}
                </p>
              </div>
            );
          })()}
          {jobDescription.questions?.length > 0 ? (
            <div className="space-y-2.5">
              {jobDescription.questions.map((question, index) => {
                const questionText = question?.nameOfQuestion || question;
                const questionType = question?.type || "general";

                const isProfileQuestion = Boolean(
                  typeof question === "object" && (
                    question.profileId ||
                    question.profile_id ||
                    question.profileQuestionId ||
                    question.profile_question_id ||
                    question.profileName ||
                    question.profile_name
                  )
                );

                const questionProfileName =
                  (typeof question === "object" && (question.profileName || question.profile_name)) ||
                  (isProfileQuestion && jobDescription.attached_profiles?.[0]?.name) ||
                  (isProfileQuestion ? "Interview Profile" : null);

                return (
                  <div key={index} className={`bg-gray-50/70 hover:bg-gray-50 p-2.5 sm:p-3 rounded-lg border border-gray-200/90 border-l-4 ${questionProfileName ? 'border-l-emerald-500' : 'border-l-blue-500'} transition-colors`}>
                    {questionProfileName && (
                      <div className="mb-2 pb-1.5 border-b border-gray-200/60 flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-300 text-[11px] font-semibold shadow-2xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                          <Layers size={11} className="text-emerald-700 stroke-[2.4] shrink-0" />
                          <span className="font-bold truncate max-w-[260px]">{questionProfileName}</span>
                        </span>
                      </div>
                    )}
                    <div className="flex items-start gap-2.5">
                      <div className="flex items-center gap-1.5 shrink-0 flex-wrap pt-0.5">
                        <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-800">
                          Q{index + 1}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium shadow-2xs ${getQuestionTypeColor(questionType)}`}>
                          {questionTypeOptions.find(opt => opt.value === questionType)?.label || questionType}
                        </span>
                        {questionType === "coding" && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
                            {question?.time_limit || 5} min
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {editingIndex === index ? (
                          <div className="space-y-2">
                            <textarea
                              value={editedQuestion}
                              onChange={(e) => setEditedQuestion(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none text-xs sm:text-sm"
                              rows="2"
                              disabled={isUpdating}
                            />
                            <select
                              value={editedType}
                              onChange={(e) => setEditedType(e.target.value)}
                              className="w-full p-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-xs sm:text-sm"
                              disabled={isUpdating}
                            >
                              {questionTypeOptions.map((type) => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                            {editedType === "coding" && (
                              <div className="flex flex-col gap-1">
                                <label className="text-xs text-gray-500">{t('jobDescription.timeLimit', 'Time Limit (minutes)')}</label>
                                <input
                                  type="number"
                                  value={editedTimeLimit}
                                  onChange={(e) => setEditedTimeLimit(Math.min(60, Math.max(1, parseInt(e.target.value) || 5)))}
                                  className="w-full p-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-xs sm:text-sm"
                                  min="1"
                                  max="60"
                                  disabled={isUpdating}
                                />
                                <span className="text-[10px] text-gray-400">{t('jobDescription.timeLimitHelper', 'Default: 5 min (Max: 60)')}</span>
                              </div>
                            )}
                            {editedType === "mcq" && (
                              <div className="space-y-2 p-2.5 bg-white rounded-lg border border-gray-200">
                                <div className="text-xs font-semibold text-gray-700">{t('jobDescription.multipleChoiceOptions', 'Multiple Choice Options')}</div>
                                <div className="grid grid-cols-1 gap-1.5">
                                  {editedMcqOptions.map((opt, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                      <input
                                        type="radio"
                                        name={`edit-mcq-correct-${index}`}
                                        checked={editedMcqCorrectIndex === i}
                                        onChange={() => setEditedMcqCorrectIndex(i)}
                                        disabled={isUpdating}
                                      />
                                      <input
                                        type="text"
                                        value={opt}
                                        onChange={(e) => setEditedMcqOptions(prev => prev.map((v, idx) => idx === i ? e.target.value : v))}
                                        className="flex-1 p-1 border border-gray-300 rounded text-xs sm:text-sm outline-none focus:ring-1 focus:ring-blue-500"
                                        placeholder={`${t('jobDescription.answer', 'Answer')} ${String.fromCharCode(65 + i)}`}
                                        disabled={isUpdating}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="flex gap-2 pt-1">
                              <button
                                onClick={() => handleSave(index)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md shadow-xs cursor-pointer transition-colors"
                                disabled={isUpdating || !editedQuestion.trim() || (editedType === "mcq" && editedMcqOptions.some(o => !o.trim()))}
                              >
                                <Save size={12} /> {t('common.save')}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingIndex(null);
                                  setEditedQuestion("");
                                  setEditedType("concept");
                                  setEditedTimeLimit(5);
                                  setEditedMcqOptions(["", "", "", ""]);
                                  setEditedMcqCorrectIndex(0);
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-gray-500 hover:bg-gray-600 text-white rounded-md shadow-xs cursor-pointer transition-colors"
                                disabled={isUpdating}
                              >
                                <X size={12} /> {t('jobDescription.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-xs sm:text-sm text-gray-800 font-medium leading-relaxed flex-1">{questionText}</span>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => handleEdit(index, question)}
                                  className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors cursor-pointer"
                                  title={t('jobDescription.editQuestion')}
                                  disabled={isUpdating}
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => setDeleteIndex(index)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                                  title={t('jobDescription.deleteQuestionTitle')}
                                  disabled={isUpdating}
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            {questionType === "mcq" && Array.isArray(question.choices) && question.choices.length > 0 && (
                              <div className="mt-2 ml-1 space-y-1">
                                {question.choices.map((choice, cIdx) => (
                                  <div key={cIdx} className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-500 font-semibold w-4">{String.fromCharCode(65 + cIdx)}.</span>
                                    <span className="text-gray-700 flex-1">{choice.text}</span>
                                    {choice.is_correct && (
                                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold">{t('jobDescription.correct')}</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="text-gray-500 text-xs mb-2.5 font-normal">{t('jobDescription.noQuestionsAdded')}</div>
              <button
                onClick={() => onAddQuestion(jobDescription.id)}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shadow-xs text-xs font-semibold mx-auto cursor-pointer"
                disabled={isUpdating}
              >
                <Plus size={14} className="stroke-[2.5]" /> {t('jobDescription.addFirstQuestion')}
              </button>
            </div>
          )}
        </div>
      </div>

      {deleteIndex !== null && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[999999] animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl transform transition-all animate-in zoom-in-95 duration-200">
            <div className="p-5">
              <div className="flex items-center justify-center w-10 h-10 mx-auto mb-3 bg-red-100 rounded-full">
                <Trash2 className="text-red-600" size={20} />
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-1 text-center">{t('jobDescription.deleteQuestion')}</h3>
              <p className="text-xs text-gray-600 mb-5 text-center">{t('jobDescription.deleteQuestionMessage')}</p>
              <div className="flex gap-2.5">
                <button
                  onClick={() => setDeleteIndex(null)}
                  className="flex-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs font-semibold cursor-pointer"
                  disabled={isUpdating}
                >
                  {t('jobDescription.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-semibold cursor-pointer"
                  disabled={isUpdating}
                >
                  {isUpdating ? t('jobDescription.deleting') : t('jobDescription.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {jobDescription?.jd_type !== 'practice' && !jobDescription?.is_practice && (
        <BenchmarksPanel jobDescription={jobDescription} />
      )}
    </div>
  );
};

export default function JobDescription({ onLogout }) {
  const { t, i18n } = useTranslation();
  const isRTL = document.documentElement.dir === 'rtl' || i18n.language === 'ar';
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const currentRequestRef = useRef(null);
  const loadedBenchmarksRef = useRef("[]");
  const questionTypeOptions = getQuestionTypeOptions(t);

  const initialFormState = {
    title: "", department: "", status: "open", employment_type: "", experience_level: "",
    required_skills: [], nice_to_have_skills: [], salary_range_min: "", salary_range_max: "",
    description: "", is_public: false, interview_style: "default", campus_questions_per_category: 3,
    questions: [], work_type: "",
    benchmarks: [], benchmarks_approve: false,
  };

  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [selectedJobDescription, setSelectedJobDescription] = useState(null);
  const [jobDescriptionForm, setJobDescriptionForm] = useState(initialFormState);
  const [jobDescriptionError, setJobDescriptionError] = useState("");
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [departmentError, setDepartmentError] = useState("");
  const [departmentSuccess, setDepartmentSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageLoading(loading, {
    message: t('jobDescription.loadingJobDescriptions', 'Loading job descriptions…'),
  });
  const [description, setDescription] = useState("");
  const [selectedJobTitle, setSelectedJobTitle] = useState("");
  const [openDescriptionDialog, setOpenDescriptionDialog] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [selectedJobForQuestion, setSelectedJobForQuestion] = useState(null);
  const [globalQuestionText, setGlobalQuestionText] = useState("");
  const [globalQuestionType, setGlobalQuestionType] = useState("concept");
  const [uploadingJD, setUploadingJD] = useState(false);
  const [globalMcqOptions, setGlobalMcqOptions] = useState(["", "", "", ""]);
  const [globalMcqCorrectIndex, setGlobalMcqCorrectIndex] = useState(0);
  const [globalTimeLimit, setGlobalTimeLimit] = useState(5);
  const [showResumeMatchingModal, setShowResumeMatchingModal] = useState(false);
  const [selectedJobForMatching, setSelectedJobForMatching] = useState(null);
  const [showAiGeneratorInCard, setShowAiGeneratorInCard] = useState(false);
  const [selectedJobForAiGenerator, setSelectedJobForAiGenerator] = useState(null);
  const [showQuestionBankPicker, setShowQuestionBankPicker] = useState(false);
  const [selectedJobForBank, setSelectedJobForBank] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [selectedJobIdForTimeline, setSelectedJobIdForTimeline] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showTogglePublicConfirm, setShowTogglePublicConfirm] = useState(false);
  const [jobToToggle, setJobToToggle] = useState(null);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [notificationAuditId, setNotificationAuditId] = useState(null);

  const initialPage = parseInt(localStorage.getItem("jobPage")) || 1;
  const initialPageSize = parseInt(localStorage.getItem("jobPageSize")) || 10;

  const [paginationData, setPaginationData] = useState({
    current_page: initialPage, page_size: initialPageSize, total_count: 0, total_pages: 1,
    has_next: false, has_previous: false, next_page: null, previous_page: null,
  });

  const [filters, setFilters] = useState({
    search: "", jd_type: "", company: "", department: "", is_public: "",
    created_date: "", status: "open", ordering: "-created_at",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const clearReviewAuditQuery = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has("review_audit_id")) {
      return;
    }

    params.delete("review_audit_id");
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const fetchJobDescriptions = useCallback(async (page = paginationData.current_page, pageSize = paginationData.page_size, options = {}) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    const controller = new AbortController();
    currentRequestRef.current = controller;

    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: pageSize.toString() });
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== null) params.append(key, value);
      });

      const res = await api.get(`job-descriptions/list_with_pagination/?${params.toString()}`, {
        headers: { Authorization: `${localStorage.getItem("adminToken")}` },
        signal: controller.signal,
      });

      const { results, pagination } = res.data;
      setJobDescriptions(Array.isArray(results) ? results : []);
      setPaginationData({
        current_page: pagination?.current_page || page,
        page_size: pagination?.page_size || pageSize,
        total_count: pagination?.total_count || 0,
        total_pages: pagination?.total_pages || 1,
        has_next: pagination?.has_next || false,
        has_previous: pagination?.has_previous || false,
        next_page: pagination?.next_page || null,
        previous_page: pagination?.previous_page || null,
      });

      localStorage.setItem("jobPage", pagination?.current_page || page);
      localStorage.setItem("jobPageSize", pagination?.page_size || pageSize);
    } catch (err) {
      // Ignore canceled requests (both AbortError and Axios CanceledError)
      if (err.name === "AbortError" || err.name === "CanceledError" || err.code === "ERR_CANCELED") {
        return;
      }
      console.error("Error fetching job descriptions:", err);
      setJobDescriptions([]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [filters, paginationData.current_page, paginationData.page_size]);

  useEffect(() => {
    api.get("departments/").then(res => setDepartments(Array.isArray(res.data) ? res.data : res.data.results || [])).catch(() => setDepartments([]));
    api.get("companies/").then(res => setCompanies(Array.isArray(res.data) ? res.data : res.data.results || [])).catch(() => setCompanies([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reviewAuditId = params.get("review_audit_id");

    if (!reviewAuditId) {
      setNotificationAuditId(null);
      return;
    }

    setNotificationAuditId(reviewAuditId);
    setShowDraftsModal(false);
    setShowAudioModal(true);
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jdId = params.get("jd");
    if (jdId) {
      api.get(`job-descriptions/${jdId}/`).then(res => {
        if (res?.data) {
          setDescription(convertMarkdownToHtml(res.data.description || ""));
          setSelectedJobTitle(res.data.title || "");
          setOpenDescriptionDialog(true);
        }
      }).catch(err => {
        console.error("Error loading JD description from URL param:", err);
      });
    }
  }, [location.search]);

  useEffect(() => {
    if (currentRequestRef.current) currentRequestRef.current.abort();
    const timer = setTimeout(() => fetchJobDescriptions(), 300);
    return () => {
      clearTimeout(timer);
      if (currentRequestRef.current) currentRequestRef.current.abort();
    };
  },// eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, paginationData.current_page, paginationData.page_size]);

  const drawerRef = useRef(null);

  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);

  const jdTypeOptions = useMemo(() => [
    { value: "", label: t('jobDescription.allTypes', 'All Types') },
    { value: "public", label: t('jobDescription.public', 'Public') },
    { value: "private", label: t('jobDescription.private', 'Private') },
  ], [t]);

  const statusOptions = useMemo(() => [
    { value: "open", label: t('jobDescription.open', 'Open') },
    { value: "closed", label: t('jobDescription.closed', 'Closed') },
    { value: "cancelled", label: t('jobDescription.cancelled', 'Cancelled') },
    { value: "expired", label: t('jobDescription.expired', 'Expired') },
    { value: "archived", label: t('jobDescription.archived', 'Archived') },
  ], [t]);

  const companyOptions = useMemo(() => [
    { value: "", label: t('jobDescription.allCompanies', 'All Companies') },
    ...companies.map((c) => ({ value: c.id.toString(), label: c.name })),
  ], [companies, t]);

  const departmentOptions = useMemo(() => [
    { value: "", label: t('jobDescription.allDepartments', 'All Departments') },
    ...departments.map((d) => ({ value: d.id.toString(), label: d.name })),
  ], [departments, t]);

  const orderingOptions = useMemo(() => [
    { value: "-created_at", label: t('jobDescription.newestFirst', 'Newest First') },
    { value: "created_at", label: t('jobDescription.oldestFirst', 'Oldest First') },
    { value: "title", label: t('jobDescription.titleAZ', 'Title (A-Z)') },
    { value: "-title", label: t('jobDescription.titleZA', 'Title (Z-A)') },
    { value: "-updated_at", label: t('jobDescription.recentlyUpdated', 'Recently Updated') },
    { value: "updated_at", label: t('jobDescription.leastRecentlyUpdated', 'Least Recently Updated') },
  ], [t]);

  const activeAdvancedFilterCount = useMemo(() => {
    let count = 0;
    if (filters.jd_type) count++;
    if (filters.status && filters.status !== "open") count++;
    if (filters.company) count++;
    if (filters.department) count++;
    if (filters.created_date) count++;
    if (filters.ordering && filters.ordering !== "-created_at") count++;
    return count;
  }, [filters]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && showAdvancedFilters) {
        setShowAdvancedFilters(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showAdvancedFilters]);

  useEffect(() => {
    if (showAdvancedFilters) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showAdvancedFilters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setPaginationData(prev => ({ ...prev, current_page: 1 }));
  };

  const clearAdvancedFilters = () => {
    setFilters(prev => ({
      ...prev,
      jd_type: "",
      company: "",
      department: "",
      created_date: "",
      status: "open",
      ordering: "-created_at",
    }));
    setPaginationData(prev => ({ ...prev, current_page: 1 }));
  };

  const clearAllFilters = () => {
    setFilters({ search: "", jd_type: "", company: "", department: "", is_public: "", created_date: "", status: "open", ordering: "-created_at" });
    setPaginationData(prev => ({ ...prev, current_page: 1 }));
  };

  const hasActiveFilters = () => {
    return Boolean(
      filters.search ||
      filters.jd_type ||
      filters.company ||
      filters.department ||
      filters.is_public !== "" ||
      filters.created_date ||
      (filters.status && filters.status !== "open") ||
      (filters.ordering && filters.ordering !== "-created_at")
    );
  };

  // Create / Add JD Menu State (Header Button)
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);

  const handleOpenCreateMenu = (event) => {
    setCreateMenuAnchor(event.currentTarget);
  };

  const handleCloseCreateMenu = () => {
    setCreateMenuAnchor(null);
  };

  const handleMenuAddManual = () => {
    handleCloseCreateMenu();
    setSelectedJobDescription(null);
    setJobDescriptionForm(initialFormState);
    setShowJobDescriptionModal(true);
  };

  const handleMenuUploadJD = () => {
    handleCloseCreateMenu();
    fileInputRef.current?.click();
  };

  const handleMenuAudioJD = () => {
    handleCloseCreateMenu();
    setShowAudioModal(true);
  };

  const handleMenuDrafts = () => {
    handleCloseCreateMenu();
    setShowDraftsModal(true);
  };

  // Action Menu State (Three Dots)
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedActionJob, setSelectedActionJob] = useState(null);

  const handleOpenActionMenu = (event, job) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedActionJob(job);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setSelectedActionJob(null);
  };

  // 1. View Description
  const handleMenuActionViewDescription = () => {
    if (!selectedActionJob) return;
    const desc = selectedActionJob.description;
    const title = selectedActionJob.title;
    handleCloseActionMenu();
    setDescription(desc);
    setSelectedJobTitle(title);
    setOpenDescriptionDialog(true);
  };

  // 2. Matching Resumes
  const handleMenuActionMatchingResumes = () => {
    if (!selectedActionJob) return;
    const job = selectedActionJob;
    handleCloseActionMenu();
    handleOpenResumeMatching(job);
  };

  // 3. Edit Job Description
  const handleMenuActionEdit = () => {
    if (!selectedActionJob) return;
    const job = selectedActionJob;
    handleCloseActionMenu();
    openEditJobDescription(job);
  };

  // 4. View Timeline
  const handleMenuActionTimeline = () => {
    if (!selectedActionJob) return;
    const jobId = selectedActionJob.id;
    handleCloseActionMenu();
    setSelectedJobIdForTimeline(jobId);
    setTimelineOpen(true);
  };

  // 5. Interview Benchmarks (toggle expansion)
  const handleMenuActionBenchmarks = () => {
    if (!selectedActionJob) return;
    const jobId = selectedActionJob.id;
    handleCloseActionMenu();
    toggleRowExpansion(jobId);
  };

  // 6. Add Questions
  const handleMenuActionAddQuestion = () => {
    if (!selectedActionJob) return;
    const jobId = selectedActionJob.id;
    handleCloseActionMenu();
    handleAddQuestion(jobId);
  };

  // 7. Archive / Unarchive
  const handleMenuActionArchive = () => {
    if (!selectedActionJob) return;
    const jobId = selectedActionJob.id;
    handleCloseActionMenu();
    if (filters.status === "archived") {
      handleUnarchive(jobId);
    } else {
      setDeleteId(jobId);
      setDialogOpen(true);
    }
  };

  const handleJobDescriptionSubmit = async (e) => {
    e.preventDefault();
    setJobDescriptionError("");
    const { jdPayload, benchmarks, approve } = splitJdFormAndBenchmarks(jobDescriptionForm);
    try {
      let jdId = selectedJobDescription?.id;
      if (selectedJobDescription) {
        await api.patch(`job-descriptions/${selectedJobDescription.id}/`, jdPayload);
        showSnackbar(t('jobDescription.jobDescriptionUpdated'));
      } else {
        const created = await api.post("job-descriptions/", jdPayload);
        jdId = created.data?.id;
        showSnackbar(t('jobDescription.jobDescriptionAdded'));
      }
      if (jdId && benchmarks.length) {
        const current = JSON.stringify(toBenchmarkPayload(benchmarks));
        const changed = current !== loadedBenchmarksRef.current;
        if (changed || approve || !selectedJobDescription) {
          try {
            await saveJdBenchmarks(api, jdId, benchmarks, { approve });
          } catch (benchErr) {
            const detail = benchErr.response?.data?.error;
            showSnackbar(
              typeof detail === 'string'
                ? detail
                : t('jobDescription.scorecardSaveFailed', 'Job saved, but the interview benchmarks could not be saved. Open the job to finish it.'),
              "error"
            );
          }
        }
      }
      setShowJobDescriptionModal(false);
      setSelectedJobDescription(null);
      setJobDescriptionForm(initialFormState);
      fetchJobDescriptions();
    } catch (err) {
      showSnackbar(selectedJobDescription ? t('jobDescription.failedToUpdateJD') : t('jobDescription.failedToAddJD'), "error");
    }
  };

  const handleArchive = async () => {
    try {
      await api.post(`job-descriptions/${deleteId}/archive/`);
      fetchJobDescriptions();
      setDialogOpen(false);
      setDeleteId(null);
      showSnackbar(t('jobDescription.jobDescriptionArchived'));
    } catch (err) {
      console.error("Error archiving job description:", err);
      showSnackbar(t('jobDescription.failedToArchiveJD'), "error");
    }
  };

  const handleUnarchive = async (jobId) => {
    try {
      await api.post(`job-descriptions/${jobId}/unarchive/`);
      fetchJobDescriptions();
      showSnackbar(t('jobDescription.jobDescriptionUnarchived'));
    } catch (err) {
      console.error("Error unarchiving job description:", err);
      showSnackbar(t('jobDescription.failedToUnarchiveJD'), "error");
    }
  };

  const openEditJobDescription = async (jobDescription) => {
    setSelectedJobDescription(jobDescription);
    const form = {
      title: jobDescription.title || "",
      department: jobDescription.department || "",
      status: jobDescription.status || "open",
      employment_type: jobDescription.employment_type || "",
      experience_level: jobDescription.experience_level || "",
      required_skills: jobDescription.required_skills || [],
      nice_to_have_skills: jobDescription.nice_to_have_skills || [],
      salary_range_min: jobDescription.salary_range_min || "",
      salary_range_max: jobDescription.salary_range_max || "",
      description: jobDescription.description || "",
      is_public: jobDescription.is_public || false,
      interview_style: jobDescription.interview_style || "default",
      campus_questions_per_category: jobDescription.campus_questions_per_category || 3,
      questions: jobDescription.questions || [],
      work_type: jobDescription.work_type || "",
      benchmarks: [],
      benchmarks_approve: false,
    };
    setJobDescriptionForm(form);
    setShowJobDescriptionModal(true);
    loadedBenchmarksRef.current = "[]";
    if (jobDescription?.id && jobDescription.jd_type !== 'practice' && !jobDescription.is_practice) {
      try {
        const res = await api.get(`job-descriptions/${jobDescription.id}/benchmarks/`);
        const loaded = res.data.benchmarks || [];
        loadedBenchmarksRef.current = JSON.stringify(toBenchmarkPayload(loaded));
        setJobDescriptionForm((prev) => ({
          ...prev,
          benchmarks: loaded,
          benchmarks_approve: false,
        }));
      } catch (err) {
        console.error("Could not load interview benchmarks for edit", err);
      }
    }
  };

  const handleUploadJD = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar(t('jobDescription.invalidFileType'), "error");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showSnackbar(t('jobDescription.fileSizeExceeded'), "error");
      return;
    }

    setUploadingJD(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('job-descriptions/parse-from-file/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        let jobDescriptionText = response.data.description || "";
        if (response.data.title) {
          const titleRegex = new RegExp(`^#{1,3}\\s*${response.data.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, "gm");
          jobDescriptionText = jobDescriptionText.replace(titleRegex, "");
        }
        const htmlContent = convertMarkdownToHtml(jobDescriptionText);

        setJobDescriptionForm({
          title: response.data.title || "",
          department: "",
          status: response.data.status || "open",
          employment_type: response.data.employment_type || "",
          experience_level: response.data.experience_level || "",
          required_skills: response.data.required_skills || [],
          nice_to_have_skills: response.data.nice_to_have_skills || [],
          salary_range_min: response.data.salary_range_min || "",
          salary_range_max: response.data.salary_range_max || "",
          description: htmlContent,
          is_public: false,
          interview_style: "default",
          campus_questions_per_category: 3,
          questions: [],
          work_type: response.data.work_type || "",
        });

        setSelectedJobDescription(null);
        setShowJobDescriptionModal(true);
        showSnackbar(t('jobDescription.jdExtractedSuccess'));
      } else {
        throw new Error('Failed to extract job description');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.error || t('jobDescription.failedToParseJD'), "error");
    } finally {
      setUploadingJD(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    try {
      const companyId = departments.length > 0 ? departments[0].company : 1;
      const res = await api.post("departments/", { name: newDepartmentName, company: companyId });
      setDepartments(prev => [...(Array.isArray(prev) ? prev : []), res.data]);
      showSnackbar(t('jobDescription.departmentAdded'));
      setShowAddDepartmentModal(false);
      setNewDepartmentName("");
    } catch (err) {
      showSnackbar(t('jobDescription.failedToAddDepartment'), "error");
    }
  };

  const handleCopyPublicLink = async (jobDescription) => {
    if (!jobDescription.is_public || !jobDescription.public_link) {
      showSnackbar(t('jobDescription.notPublicOrNoLink'), "warning");
      return;
    }

    try {
      await navigator.clipboard.writeText(jobDescription.public_link);
      setCopiedId(jobDescription.id);
      showSnackbar(t('jobDescription.publicLinkCopied'));
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      showSnackbar(t('jobDescription.failedToCopyLink'), "error");
    }
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      const opening = !next.has(id);
      if (opening) next.add(id);
      else next.delete(id);
      if (opening) {
        window.setTimeout(() => {
          document.getElementById(`jd-benchmarks-${id}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 50);
      }
      return next;
    });
  };

  const handleAddQuestion = (jobId) => {
    const job = jobDescriptions.find(j => j.id === jobId);
    setSelectedJobForQuestion(job);
    setShowAddQuestionModal(true);
    setGlobalQuestionText("");
    setGlobalQuestionType("concept");
    setGlobalMcqOptions(["", "", "", ""]);
    setGlobalMcqCorrectIndex(0);
    setGlobalTimeLimit(5);
  };

  const handleUpdateQuestions = (jobId, updatedQuestions) => {
    setJobDescriptions(prevJobs => prevJobs.map(job => job.id === jobId ? { ...job, questions: updatedQuestions } : job));
  };

  const handleGenerateAiQuestion = (job) => {
    setSelectedJobForAiGenerator(job);
    setShowAiGeneratorInCard(true);
  };

  const handleOpenQuestionBank = (job) => {
    setSelectedJobForBank(job);
    setShowQuestionBankPicker(true);
  };

  const handleQuestionBankAdded = async (newQuestions) => {
    const targetJob = selectedJobForBank || selectedJobForQuestion;
    if (!targetJob) return;

    try {
      const updatedQuestions = [...(targetJob.questions || []), ...newQuestions];
      await api.put(`job-descriptions/${targetJob.id}/`, { questions: updatedQuestions });
      handleUpdateQuestions(targetJob.id, updatedQuestions);
      setShowQuestionBankPicker(false);
      setSelectedJobForBank(null);
      setShowAddQuestionModal(false);
      setSelectedJobForQuestion(null);
      showSnackbar(t('jobDescription.questionAdded'));
    } catch (error) {
      showSnackbar(t('jobDescription.failedToAddQuestion'), "error");
    }
  };

  const handleAiQuestionsAddedInCard = async (newQuestions) => {
    if (!selectedJobForAiGenerator) return;

    try {
      const updatedQuestions = [...(selectedJobForAiGenerator.questions || []), ...newQuestions];
      await api.put(`job-descriptions/${selectedJobForAiGenerator.id}/`, { questions: updatedQuestions });
      handleUpdateQuestions(selectedJobForAiGenerator.id, updatedQuestions);
      setShowAiGeneratorInCard(false);
      setSelectedJobForAiGenerator(null);
      showSnackbar(t('jobDescription.questionAdded'));
    } catch (error) {
      showSnackbar(t('jobDescription.failedToAddQuestion'), "error");
    }
  };

  const handleOpenResumeMatching = (job) => {
    setSelectedJobForMatching(job);
    setShowResumeMatchingModal(true);
  };

  const handleStartResumeMatching = async (topN) => {
    try {
      const res = await triggerSemanticMatching(selectedJobForMatching.id);

      showSnackbar(t('jobDescription.resumeMatchingStarted'));
      setShowResumeMatchingModal(false);
      const jobId = selectedJobForMatching.id;
      setSelectedJobForMatching(null);
      navigate(`/admin/matching-resumes/${jobId}/${res.task_id}?limit=${topN}`);
    } catch (error) {
      showSnackbar(t('jobDescription.failedToStartMatching'), "error");
    }
  };

  const pollJdStatuses = useCallback(async (ids) => {
    const updates = await Promise.all(ids.map(async (id) => {
      try {
        return await getJdMatchingStatus(id);
      } catch (err) {
        console.error(`Error polling JD ${id} status:`, err);
        return null;
      }
    }));

    const byId = new Map(
      updates.filter((update) => update && update.id != null).map((update) => [update.id, update])
    );
    if (byId.size === 0) return;

    setJobDescriptions((prev) => {
      let changed = false;
      const next = prev.map((jd) => {
        const update = byId.get(jd.id);
        if (!update) return jd;
        if (
          jd.matching_status === update.matching_status &&
          jd.scheduling_status === update.scheduling_status &&
          jd.processed_items_count === update.processed_items_count &&
          jd.total_items_count === update.total_items_count
        ) {
          return jd;
        }
        changed = true;
        return {
          ...jd,
          matching_status: update.matching_status,
          scheduling_status: update.scheduling_status,
          processed_items_count: update.processed_items_count,
          total_items_count: update.total_items_count,
        };
      });
      return changed ? next : prev;
    });
  }, []);

  const processingKey = useMemo(
    () => jobDescriptions.filter(isJdBusy).map((jd) => jd.id).sort((a, b) => a - b).join(','),
    [jobDescriptions]
  );

  useEffect(() => {
    if (!processingKey) return undefined;
    const ids = processingKey.split(',').map(Number);

    pollJdStatuses(ids);
    const interval = setInterval(() => {
      pollJdStatuses(ids);
    }, 5000);

    return () => clearInterval(interval);
  }, [processingKey, pollJdStatuses]);

  const handlePageChange = (newPage) => {
    setPaginationData(prev => ({ ...prev, current_page: newPage }));
    localStorage.setItem("jobPage", newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPaginationData(prev => ({ ...prev, page_size: newSize, current_page: 1 }));
    localStorage.setItem("jobPage", 1);
    localStorage.setItem("jobPageSize", newSize);
  };

  const truncateUrl = (url, maxLength = 50) => {
    if (!url || url.length <= maxLength) return url || "";
    return url.substring(0, maxLength) + "...";
  };

  const handleAddGlobalQuestion = async () => {
    const question = globalQuestionText.trim();
    if (!question) return;

    try {
      const newQuestion = {
        nameOfQuestion: question,
        type: globalQuestionType,
        ...(globalQuestionType === "coding" && { time_limit: globalTimeLimit > 0 ? globalTimeLimit : 5 })
      };

      if (globalQuestionType === "mcq") {
        const trimmed = globalMcqOptions.map(s => s.trim());
        if (trimmed.some(s => !s) || globalMcqCorrectIndex < 0 || globalMcqCorrectIndex > 3) return;
        newQuestion.choices = trimmed.map((text, i) => ({ text, is_correct: i === globalMcqCorrectIndex }));
      }

      const updatedQuestions = [...(selectedJobForQuestion.questions || []), newQuestion];
      await api.put(`job-descriptions/${selectedJobForQuestion.id}/`, { questions: updatedQuestions });
      handleUpdateQuestions(selectedJobForQuestion.id, updatedQuestions);
      setShowAddQuestionModal(false);
      setSelectedJobForQuestion(null);
      setGlobalQuestionText("");
      setGlobalQuestionType("concept");
      setGlobalTimeLimit(5);
      setGlobalMcqOptions(["", "", "", ""]);
      setGlobalMcqCorrectIndex(0);
      showSnackbar(t('jobDescription.questionAdded'));
    } catch (error) {
      showSnackbar(t('jobDescription.failedToAddQuestion'), "error");
    }
  };

  const handleTogglePublicClick = (jobDescription) => {
    setJobToToggle(jobDescription);
    setShowTogglePublicConfirm(true);
  };

  const confirmTogglePublic = async () => {
    if (!jobToToggle) return;

    try {
      const newIsPublic = !jobToToggle.is_public;
      const response = await api.patch(`job-descriptions/${jobToToggle.id}/`, { is_public: newIsPublic });

      // Update state with the full response data including public_link
      const updatedJobData = response.data?.data || {};
      setJobDescriptions(prevJobs =>
        prevJobs.map(job =>
          job.id === jobToToggle.id
            ? {
              ...job,
              is_public: updatedJobData.is_public ?? newIsPublic,
              public_link: updatedJobData.public_link ?? job.public_link,
              jd_type: updatedJobData.jd_type ?? job.jd_type
            }
            : job
        )
      );
      showSnackbar(
        newIsPublic
          ? t('jobDescription.jobMadePublic')
          : t('jobDescription.jobMadePrivate')
      );
    } catch (error) {
      showSnackbar(t('jobDescription.statusUpdateFailed'), "error");
    } finally {
      setShowTogglePublicConfirm(false);
      setJobToToggle(null);
    }
  };

  const handleAudioJDApply = (data) => {
    if (!data) return;

    setJobDescriptionForm({
      ...initialFormState,
      title: data.title || "",
      description: data.description || "",
      employment_type: data.employment_type || "",
      experience_level: data.experience_level || "",
      required_skills: data.required_skills || data.required_skills_list || [],
      nice_to_have_skills: data.nice_to_have_skills || data.nice_to_have_skills_list || [],
      questions: data.questions || data.interview_questions || [],
      work_type: data.work_type || "on_site",
      salary_range_min: data.salary_range_min || "",
      salary_range_max: data.salary_range_max || "",
    });

    setSelectedJobDescription(null);
    setShowAudioModal(false);
    setNotificationAuditId(null);
    clearReviewAuditQuery();
    setShowJobDescriptionModal(true);
  };

  const handleCloseAudioModal = () => {
    setShowAudioModal(false);

    if (!notificationAuditId) {
      return;
    }

    setNotificationAuditId(null);
    clearReviewAuditQuery();
  };

  const handleSelectDraft = async (draftId) => {
    try {
      setLoading(true);
      const res = await pollJDGenerationStatus(draftId);

      if (res.status === 'done' && res.structured_description) {
        const sd = res.structured_description;

        // Convert markdown to HTML for the description field, excluding the title
        let rawDescription = res.generated_description || '';
        if (sd.title) {
          const titleRegex = new RegExp(`^#{1,3}\\s*${sd.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'gm');
          rawDescription = rawDescription.replace(titleRegex, '');
        }
        const htmlDescription = convertMarkdownToHtml(rawDescription);

        setJobDescriptionForm({
          ...initialFormState,
          title: sd.title || "",
          description: htmlDescription,
          employment_type: sd.employment_type || "",
          experience_level: sd.experience_level || "",
          required_skills: sd.required_skills_list || sd.required_skills || [],
          nice_to_have_skills: sd.nice_to_have_skills_list || sd.nice_to_have_skills || [],
          questions: sd.interview_questions || sd.questions || [],
          work_type: sd.work_type || "on_site",
          salary_range_min: sd.salary_range_min || "",
          salary_range_max: sd.salary_range_max || "",
        });

        setSelectedJobDescription(null);
        setShowDraftsModal(false);
        setShowJobDescriptionModal(true);
      } else {
        showSnackbar(t('jobDescription.draftNotReady', 'Draft is not ready yet or failed.'), "error");
      }
    } catch (error) {
      console.error('Error fetching draft:', error);
      showSnackbar(t('jobDescription.failedToLoadDraft', 'Failed to load draft details.'), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell className="bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
      <NotificationPopup open={snackbar.open} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} message={snackbar.message} />
      <PageCard>
          <PageHeader
            title={t('jobDescription.title', 'Job Descriptions')}
            subtitle={t('jobDescription.pageSubtitle', 'Manage job descriptions for open positions')}
          >
                <input type="file" ref={fileInputRef} accept=".pdf,.doc,.docx" onChange={handleUploadJD} className="hidden" id="jd-file-upload" />
                <PageHeaderButton
                  id="create-jd-menu-button"
                  aria-controls={Boolean(createMenuAnchor) ? 'create-jd-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={Boolean(createMenuAnchor) ? 'true' : undefined}
                  onClick={handleOpenCreateMenu}
                  title={t('jobDescription.addJobDescription', 'Add Job Description')}
                  icon={uploadingJD ? <CircularProgress size={13} color="inherit" /> : <Plus size={15} />}
                >
                  {t('jobDescription.addJobDescription', 'Add Job Description')}
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${Boolean(createMenuAnchor) ? 'rotate-180' : ''}`}
                  />
                </PageHeaderButton>
          </PageHeader>

          {/* Advanced Filters Bar & Drawer */}
          <div className="px-4 py-1.5 sm:px-6 sm:py-2 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50/30 shrink-0">
            <div className="space-y-1.5">
              <div className="flex flex-col sm:flex-row gap-2.5 items-start sm:items-center">
                <div className="flex items-center gap-2 flex-1 w-full">
                  <div className="relative flex-1">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => handleFilterChange("search", e.target.value)}
                      placeholder={t('jobDescription.searchPlaceholder')}
                      className="w-full pl-9 pr-8 h-8.5 sm:h-9 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-2xs hover:border-gray-400 font-medium text-xs sm:text-sm text-gray-800"
                    />
                    {filters.search && (
                      <button
                        type="button"
                        onClick={() => handleFilterChange("search", "")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded cursor-pointer"
                        title="Clear search"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFilters(true)}
                    className={`inline-flex items-center gap-1.5 px-3 h-8.5 sm:h-9 border rounded-lg transition-all duration-200 font-semibold shadow-2xs hover:shadow-xs cursor-pointer text-xs sm:text-sm whitespace-nowrap ${activeAdvancedFilterCount > 0 || showAdvancedFilters
                        ? "bg-blue-50 text-blue-700 border-blue-400 shadow-xs"
                        : "bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600"
                      }`}
                  >
                    <Filter size={14} className={activeAdvancedFilterCount > 0 ? "text-blue-600" : "text-gray-500"} />
                    <span>{t('jobDescription.advancedFilters', 'Advanced Filters')}</span>
                    {activeAdvancedFilterCount > 0 && (
                      <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-blue-600 rounded-full shadow-xs">
                        {activeAdvancedFilterCount}
                      </span>
                    )}
                  </button>

                  {activeAdvancedFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearAdvancedFilters}
                      className="inline-flex items-center justify-center w-8.5 h-8.5 sm:w-9 sm:h-9 text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors cursor-pointer shadow-2xs"
                      title={t('jobDescription.clearAdvancedFilters', 'Clear advanced filters')}
                      aria-label="Clear advanced filters"
                    >
                      <X size={15} className="stroke-[2.5]" />
                    </button>
                  )}
                </div>
              </div>

              {/* Active Filter Chips */}
              {hasActiveFilters() && (
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <span className="text-gray-600 font-semibold text-xs select-none">{t('jobDescription.activeFilters')}:</span>
                  {filters.search && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium shadow-2xs">
                      <span>{t('jobDescription.search')}: "{filters.search}"</span>
                      <button type="button" onClick={() => handleFilterChange("search", "")} className="hover:bg-blue-200 rounded-full p-0.5 transition-colors cursor-pointer">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.jd_type && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium shadow-2xs">
                      <span>{t('jobDescription.jdType')}: {filters.jd_type}</span>
                      <button type="button" onClick={() => handleFilterChange("jd_type", "")} className="hover:bg-emerald-200 rounded-full p-0.5 transition-colors cursor-pointer">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.status && filters.status !== "open" && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-cyan-100 text-cyan-800 rounded-full text-xs font-medium shadow-2xs">
                      <span>{t('jobDescription.status')}: {filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}</span>
                      <button type="button" onClick={() => handleFilterChange("status", "open")} className="hover:bg-cyan-200 rounded-full p-0.5 transition-colors cursor-pointer">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.company && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium shadow-2xs">
                      <span>{t('jobDescription.company')}: {companies.find(c => c.id.toString() === filters.company)?.name || filters.company}</span>
                      <button type="button" onClick={() => handleFilterChange("company", "")} className="hover:bg-indigo-200 rounded-full p-0.5 transition-colors cursor-pointer">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.department && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-violet-100 text-violet-800 rounded-full text-xs font-medium shadow-2xs">
                      <span>{t('jobDescription.department')}: {departments.find(d => d.id.toString() === filters.department)?.name || filters.department}</span>
                      <button type="button" onClick={() => handleFilterChange("department", "")} className="hover:bg-violet-200 rounded-full p-0.5 transition-colors cursor-pointer">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.created_date && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-xs font-medium shadow-2xs">
                      <span>{t('jobDescription.createdDate')}: {filters.created_date}</span>
                      <button type="button" onClick={() => handleFilterChange("created_date", "")} className="hover:bg-pink-200 rounded-full p-0.5 transition-colors cursor-pointer">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.ordering && filters.ordering !== "-created_at" && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium shadow-2xs">
                      <span>{t('jobDescription.sortBy')}: {orderingOptions.find(o => o.value === filters.ordering)?.label || filters.ordering}</span>
                      <button type="button" onClick={() => handleFilterChange("ordering", "-created_at")} className="hover:bg-amber-200 rounded-full p-0.5 transition-colors cursor-pointer">
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="text-xs text-red-600 hover:text-red-800 font-semibold underline ml-1 cursor-pointer"
                  >
                    {t('jobDescription.clearAll', 'Clear All')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Advanced Filters Slide-Over Drawer matching Interview Overview */}
          <Drawer
            anchor={isRTL ? 'left' : 'right'}
            open={Boolean(showAdvancedFilters)}
            onClose={() => setShowAdvancedFilters(false)}
            transitionDuration={{ enter: 350, exit: 250 }}
            SlideProps={{
              easing: {
                enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
                exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
              },
            }}
            sx={{
              zIndex: 1350,
              '& .MuiBackdrop-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(2px)',
              },
              '& .MuiDrawer-paper': {
                width: { xs: '100%', sm: '440px' },
                maxWidth: '100%',
                boxSizing: 'border-box',
                background: '#ffffff',
                boxShadow: isRTL
                  ? '10px 0 40px rgba(0, 0, 0, 0.18)'
                  : '-10px 0 40px rgba(0, 0, 0, 0.18)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              },
            }}
          >
            {/* Drawer Header with Blue Gradient */}
            <div
              className="flex items-center justify-between px-6 py-4 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-xs flex items-center justify-center text-white shrink-0">
                  <Filter size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white tracking-tight leading-tight">
                    {t('jobDescription.advancedFilters', 'Advanced Filters')}
                  </h2>
                  <p className="text-[11px] text-blue-100">
                    {t('jobDescription.refineResults', 'Filter and refine your results')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAdvancedFilters(false)}
                className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Drawer Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {/* 1. JD Type */}
              <PremiumSelect
                label={t('jobDescription.jdType', 'JD Type')}
                icon={FileText}
                iconColor="text-blue-600"
                value={filters.jd_type || ''}
                onChange={(val) => handleFilterChange('jd_type', val)}
                options={jdTypeOptions}
                placeholder={t('jobDescription.allTypes', 'All Types')}
                searchable={true}
                className="w-full"
                menuWidth="w-full"
              />

              {/* 2. Status */}
              <PremiumSelect
                label={t('jobDescription.status', 'Status')}
                icon={CheckCircle2}
                iconColor="text-emerald-600"
                value={filters.status || 'open'}
                onChange={(val) => handleFilterChange('status', val)}
                options={statusOptions}
                placeholder={t('jobDescription.open', 'Open')}
                searchable={true}
                className="w-full"
                menuWidth="w-full"
              />

              {/* 3. Company */}
              <PremiumSelect
                label={t('jobDescription.company', 'Company')}
                icon={Building2}
                iconColor="text-indigo-600"
                value={filters.company || ''}
                onChange={(val) => handleFilterChange('company', val)}
                options={companyOptions}
                placeholder={t('jobDescription.allCompanies', 'All Companies')}
                searchable={true}
                className="w-full"
                menuWidth="w-full"
              />

              {/* 4. Department */}
              <PremiumSelect
                label={t('jobDescription.department', 'Department')}
                icon={Users}
                iconColor="text-violet-600"
                value={filters.department || ''}
                onChange={(val) => handleFilterChange('department', val)}
                options={departmentOptions}
                placeholder={t('jobDescription.allDepartments', 'All Departments')}
                searchable={true}
                className="w-full"
                menuWidth="w-full"
              />

              {/* 5. Created Date */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1.5 select-none">
                  <Calendar size={13} className="text-pink-600" />
                  <span>{t('jobDescription.createdDate', 'Created Date')}</span>
                </label>
                <PremiumDatePicker
                  value={filters.created_date || ''}
                  max={todayStr}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val && val > todayStr) {
                      handleFilterChange('created_date', todayStr);
                    } else {
                      handleFilterChange('created_date', val);
                    }
                  }}
                  placeholder="dd-mm-yyyy"
                />
              </div>

              {/* 6. Sort By */}
              <PremiumSelect
                label={t('jobDescription.sortBy', 'Sort By')}
                icon={ArrowUpDown}
                iconColor="text-amber-600"
                value={filters.ordering || '-created_at'}
                onChange={(val) => handleFilterChange('ordering', val)}
                options={orderingOptions}
                placeholder={t('jobDescription.newestFirst', 'Newest First')}
                searchable={true}
                className="w-full"
                menuWidth="w-full"
              />
            </div>

            {/* Drawer Footer matching Interview Overview */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
              <button
                type="button"
                onClick={clearAdvancedFilters}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              >
                {t('jobDescription.clearFilters', 'Clear filters')}
              </button>
              <button
                type="button"
                onClick={() => setShowAdvancedFilters(false)}
                className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm cursor-pointer"
              >
                {t('jobDescription.applyFilters', 'Apply filters')}
              </button>
            </div>
          </Drawer>

          {/* Content */}
          <div className="p-6 flex-1 min-h-0 overflow-hidden flex flex-col">
            {loading ? null : jobDescriptions.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto mb-6 w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center shadow-lg">
                  <Eye size={32} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{t('jobDescription.noJobDescriptions')}</h3>
                <p className="text-gray-600 mb-4 text-sm">{hasActiveFilters() ? t('jobDescription.adjustFilters') : t('jobDescription.addFirstJD')}</p>
                {hasActiveFilters() && (
                  <button onClick={clearAllFilters} className="px-5 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold shadow-md text-sm cursor-pointer">
                    {t('jobDescription.clearAllFilters')}
                  </button>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 shadow-xs overflow-x-hidden overflow-y-auto flex-1 min-h-0 bg-white">
                <table className="w-full table-fixed bg-white text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-20 shadow-xs">
                    <tr>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[30%] bg-gray-50">{t('jobDescription.tableTitle')}</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[18%] bg-gray-50">{t('jobDescription.department')}</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[15%] bg-gray-50">{t('jobDescription.tableInterviewStyle')}</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[12%] bg-gray-50">{t('jobDescription.tablePublic')}</th>
                      <th className="text-left py-2.5 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[17%] bg-gray-50">{t('jobDescription.tablePublicLink')}</th>
                      <th className="text-center py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[8%] bg-gray-50">{t('jobDescription.tableActions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobDescriptions.map((row, index) => (
                      <React.Fragment key={row.id}>
                        <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/40 transition-colors duration-150 group`}>
                          <td className="py-2.5 px-4 align-middle overflow-hidden">
                            <div
                              className="cursor-pointer min-w-0 max-w-full"
                              onClick={() => toggleRowExpansion(row.id)}
                            >
                              <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                                <OverflowTooltip
                                  text={row.title}
                                  className="min-w-0 shrink text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors truncate"
                                />
                                {Array.isArray(row.attached_profiles) && row.attached_profiles.length > 0 && (
                                  row.attached_profiles.slice(0, 1).map((prof) => (
                                    <Tooltip
                                      key={prof.id}
                                      title={`Attached Profile: ${prof.name}${prof.target_role ? ` (${prof.target_role})` : ''}`}
                                      placement="top"
                                      enterDelay={200}
                                      disableInteractive
                                      arrow
                                      slotProps={hoverTipSlotProps}
                                    >
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200/60 tracking-wide shrink-0 max-w-[140px]">
                                        <Layers className="w-2.5 h-2.5 text-purple-500 shrink-0" />
                                        <span className="truncate">{prof.name}</span>
                                      </span>
                                    </Tooltip>
                                  ))
                                )}
                              </div>
                              {row.created_at && (
                                <div className="text-xs text-gray-500 mt-0.5 font-normal">
                                  {formatDate(row.created_at)}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 px-4 align-middle overflow-hidden">
                            {row.department_name ? (
                              <OverflowTooltip
                                text={row.department_name}
                                className="block text-sm text-gray-700 font-normal truncate"
                              />
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-4 align-middle">
                            <div className="flex flex-col items-start gap-1">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium shadow-2xs whitespace-nowrap w-fit ${
                                row.interview_style === "campus_recruitment"
                                  ? "bg-purple-50 text-purple-700 border border-purple-200"
                                  : row.interview_style === "startup"
                                  ? "bg-orange-50 text-orange-700 border border-orange-200"
                                  : row.interview_style === "enterprise"
                                  ? "bg-blue-50 text-blue-700 border border-blue-200"
                                  : "bg-gray-50 text-gray-700 border border-gray-200"
                              }`}>
                                {row.interview_style === "campus_recruitment" ? t('jobDescription.campus') : row.interview_style === "startup" ? t('jobDescription.startup') : row.interview_style === "enterprise" ? t('jobDescription.enterprise') : t('jobDescription.default')}
                              </span>
                              {row.interview_style === "campus_recruitment" && row.campus_questions_per_category && (
                                <span className="text-xs text-gray-500 font-normal whitespace-nowrap">{row.campus_questions_per_category} {t('jobDescription.form.perCategory', 'per Category')}</span>
                              )}
                            </div>
                          </td>
                          <td className="py-2.5 px-4 align-middle">
                            <div className="group relative inline-block">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium shadow-2xs whitespace-nowrap transition-all duration-150 ${
                                row.is_public
                                  ? "bg-green-50 text-green-700 border border-green-200 group-hover:bg-green-100"
                                  : "bg-amber-50 text-amber-700 border border-amber-200 group-hover:bg-amber-100"
                              }`}>
                                {row.is_public ? t('jobDescription.public') : t('jobDescription.private')}
                              </span>
                              <div className="absolute left-0 top-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none group-hover:pointer-events-auto z-20">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTogglePublicClick(row);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-0.5 rounded-md text-xs font-medium whitespace-nowrap shadow-md cursor-pointer transition-all"
                                  title={row.is_public ? t('jobDescription.makePrivate') : t('jobDescription.makePublic')}
                                >
                                  {row.is_public ? t('jobDescription.makePrivate') : t('jobDescription.makePublic')}
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="py-2.5 px-4 align-middle">
                            {row.is_public && row.public_link ? (
                              <div className="flex items-center gap-1.5 max-w-full">
                                <div className="flex-1 min-w-0">
                                  <div
                                    className="text-xs text-gray-600 font-mono truncate cursor-pointer hover:text-blue-600 transition-colors font-normal"
                                    title={row.public_link}
                                    onClick={() => window.open(row.public_link, "_blank")}
                                  >
                                    {truncateUrl(row.public_link, 22)}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleCopyPublicLink(row)}
                                  className={`p-1 rounded-md transition-all shrink-0 ${
                                    copiedId === row.id
                                      ? "text-green-700 bg-green-100"
                                      : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                                  }`}
                                  title={t('jobDescription.copyPublicLink')}
                                >
                                  {copiedId === row.id ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400 font-normal">
                                {row.is_public ? t('jobDescription.noPublicLink') : t('jobDescription.notPublic')}
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 align-middle text-center whitespace-nowrap">
                            {row.matching_status?.toLowerCase() === 'processing' ? (
                              <div
                                className="inline-flex items-center justify-center p-1.5 text-blue-600 bg-blue-50 rounded-lg animate-pulse"
                                title={t('jobDescription.buttons.systemBusy')}
                              >
                                <Loader2 size={16} className="animate-spin" />
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => handleOpenActionMenu(e, row)}
                                className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors cursor-pointer ${
                                  selectedActionJob?.id === row.id && Boolean(actionMenuAnchor)
                                    ? 'bg-blue-100 text-blue-700 shadow-xs'
                                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                                }`}
                                title={t('jobDescription.tableActions', 'Actions')}
                                aria-label="Actions"
                              >
                                <MoreVertical size={18} />
                              </button>
                            )}
                          </td>
                        </tr>
                        {expandedRows.has(row.id) && (
                          <tr className="bg-white">
                            <td colSpan={6} className="p-0">
                              <Collapse in={expandedRows.has(row.id)} timeout={0} unmountOnExit>
                                <div id={`jd-benchmarks-${row.id}`} className="max-w-full min-w-0">
                                  <JobDescriptionCard jobDescription={row} onAddQuestion={handleAddQuestion} onUpdateQuestions={handleUpdateQuestions} onGenerateAiQuestion={handleGenerateAiQuestion} onAddFromQuestionBank={handleOpenQuestionBank} />
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination pinned flush at the bottom of the card - ZERO extra bottom space */}
          <div className="bg-white border-t border-gray-200 z-10 rounded-b-xl shrink-0">
            <Pagination
              currentPage={paginationData.current_page}
              totalPages={paginationData.total_pages}
              totalCount={paginationData.total_count}
              currentCount={jobDescriptions.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSize={paginationData.page_size}
              showPageSize={false}
              hasActiveFilters={hasActiveFilters()}
              fixed={false}
              className="py-0"
            />
          </div>
      </PageCard>

      {/* Modals */}
      <AddJobDescriptionDialog
        open={showJobDescriptionModal}
        onClose={() => { setShowJobDescriptionModal(false); setSelectedJobDescription(null); setJobDescriptionForm(initialFormState); setJobDescriptionError(""); }}
        onSubmit={handleJobDescriptionSubmit}
        jobDescriptionForm={jobDescriptionForm}
        handleInputChange={(e) => setJobDescriptionForm(prev => ({ ...prev, [e.target.name]: e.target.value }))}
        departments={departments}
        selectedJobDescription={selectedJobDescription}
        jobDescriptionError={jobDescriptionError}
        setShowAddDepartmentModal={setShowAddDepartmentModal}
      />

      <AddDepartmentModal
        open={showAddDepartmentModal}
        onClose={() => { setShowAddDepartmentModal(false); setDepartmentError(""); setDepartmentSuccess(""); }}
        onSubmit={handleAddDepartment}
        departmentName={newDepartmentName}
        setDepartmentName={setNewDepartmentName}
        departmentError={departmentError}
        departmentSuccess={departmentSuccess}
      />

      {dialogOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[999999] animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl transform transition-all animate-in zoom-in-95 duration-200">
            <div className="p-8">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl shadow-lg">
                <IoArchiveOutline className="text-red-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">{t('jobDescription.archiveJD')}</h3>
              <p className="text-gray-600 mb-8 text-center text-sm leading-relaxed">{t('jobDescription.archiveJDMessage')}</p>
              <div className="flex gap-4">
                <button
                  onClick={() => { setDialogOpen(false); setDeleteId(null); }}
                  className="flex-1 px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md"
                >
                  {t('jobDescription.cancel')}
                </button>
                <button
                  onClick={handleArchive}
                  className="flex-1 px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl"
                >
                  {t('jobDescription.archive')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTogglePublicConfirm && jobToToggle && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[999999] animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl transform transition-all animate-in zoom-in-95 duration-200">
            <div className="p-8">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl shadow-lg">
                <Eye className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">
                {jobToToggle.is_public ? t('jobDescription.makePrivate') : t('jobDescription.makePublic')}
              </h3>
              <p className="text-gray-600 mb-8 text-center text-sm leading-relaxed">
                {jobToToggle.is_public
                  ? t('jobDescription.confirmMakePrivate')
                  : t('jobDescription.confirmMakePublic')}
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowTogglePublicConfirm(false);
                    setJobToToggle(null);
                  }}
                  className="flex-1 px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md"
                >
                  {t('jobDescription.cancel')}
                </button>
                <button
                  onClick={confirmTogglePublic}
                  className="flex-1 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl"
                >
                  {jobToToggle.is_public ? t('jobDescription.makePrivate') : t('jobDescription.makePublic')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ResumeMatchingModal open={showResumeMatchingModal} onClose={() => { setShowResumeMatchingModal(false); setSelectedJobForMatching(null); }} onSubmit={handleStartResumeMatching} jobTitle={selectedJobForMatching?.title || ""} />

      {showAddQuestionModal && selectedJobForQuestion && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-[999999] animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl transform transition-all animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('jobDescription.addQuestionTitle')}</h3>
                  <p className="text-gray-600 mt-1 text-sm font-medium">{selectedJobForQuestion.title}</p>
                </div>
                <button
                  onClick={() => { setShowAddQuestionModal(false); setSelectedJobForQuestion(null); }}
                  className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="space-y-6">
                {(selectedJobForQuestion.questions?.length || 0) === 0 && (
                  <div className="mb-6 space-y-3">
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl border-2 border-indigo-100 flex items-center justify-between shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-white rounded-xl text-indigo-600 shadow-sm border border-indigo-100">
                          <LibraryBooksOutlinedIcon sx={{ fontSize: 20 }} />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-gray-900">{t('questionBank.addFromBank', 'Add from Question Bank')}</div>
                          <div className="text-xs text-gray-600 mt-0.5">{t('questionBank.subtitle', 'Create and reuse interview questions for your company')}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowAddQuestionModal(false);
                          handleOpenQuestionBank(selectedJobForQuestion);
                        }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-semibold hover:bg-indigo-700 transition-all shadow-md"
                      >
                        {t('questionBank.addFromBank', 'Add from Question Bank')}
                      </button>
                    </div>
                    <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-100 flex items-center justify-between shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-white rounded-xl text-blue-600 shadow-sm border border-blue-100">
                          <AutoAwesomeOutlinedIcon sx={{ fontSize: 20 }} />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-gray-900">{t('jobDescription.generateWithAI', 'Generate with AI')}</div>
                          <div className="text-xs text-gray-600 mt-0.5">{t('jobDescription.aiQuestionSubtitle', 'Use AI to generate tailored interview questions.')}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowAddQuestionModal(false);
                          handleGenerateAiQuestion(selectedJobForQuestion);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-xs font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg active:scale-95"
                      >
                        {t('jobDescription.generateQuestions', 'Generate Questions')}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-gray-700 mb-3 font-semibold text-sm">{t('jobDescription.question')}</label>
                  <textarea
                    placeholder={t('jobDescription.questionPlaceholder')}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none transition-all bg-white shadow-sm hover:shadow-md text-sm"
                    rows="4"
                    value={globalQuestionText}
                    onChange={(e) => setGlobalQuestionText(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-3 font-semibold text-sm">{t('jobDescription.questionType')}</label>
                  <select
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm hover:shadow-md text-sm"
                    value={globalQuestionType}
                    onChange={(e) => setGlobalQuestionType(e.target.value)}
                  >
                    {questionTypeOptions.map((type) => (<option key={type.value} value={type.value}>{type.label}</option>))}
                  </select>
                </div>
                {globalQuestionType === "coding" && (
                  <div>
                    <label className="block text-gray-700 mb-3 font-semibold text-sm">{t('jobDescription.timeLimit', 'Time Limit (minutes)')}</label>
                    <input
                      type="number"
                      className="w-full p-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white shadow-sm hover:shadow-md text-sm"
                      value={globalTimeLimit}
                      onChange={(e) => setGlobalTimeLimit(Math.min(60, Math.max(1, parseInt(e.target.value) || 5)))}
                      min="1"
                      max="60"
                    />
                    <p className="text-xs text-gray-500 mt-2">{t('jobDescription.timeLimitHelper', 'Default: 5 min (Max: 60)')}</p>
                  </div>
                )}
                {globalQuestionType === "mcq" && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-2xl border-2 border-gray-200">
                    <div className="text-sm text-gray-700 font-semibold">{t('jobDescription.multipleChoiceOptions')}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {globalMcqOptions.map((opt, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all">
                          <input
                            type="radio"
                            name="global-mcq-correct"
                            checked={globalMcqCorrectIndex === i}
                            onChange={() => setGlobalMcqCorrectIndex(i)}
                            aria-label={`Mark answer ${String.fromCharCode(65 + i)} correct`}
                            className="w-5 h-5 text-blue-600"
                          />
                          <input
                            type="text"
                            className="flex-1 p-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                            placeholder={`${t('jobDescription.answer')} ${String.fromCharCode(65 + i)}`}
                            value={opt}
                            onChange={(e) => setGlobalMcqOptions(prev => prev.map((v, idx) => (idx === i ? e.target.value : v)))}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-4 pt-4 border-t-2 border-gray-200">
                  <button
                    onClick={() => { setShowAddQuestionModal(false); setSelectedJobForQuestion(null); setGlobalQuestionText(""); setGlobalQuestionType("concept"); setGlobalMcqOptions(["", "", "", ""]); setGlobalMcqCorrectIndex(0); }}
                    className="flex-1 px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 text-sm font-semibold shadow-sm hover:shadow-md"
                  >
                    {t('jobDescription.cancel')}
                  </button>
                  <button
                    onClick={handleAddGlobalQuestion}
                    className="flex-1 px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 text-sm font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!globalQuestionText.trim() || (globalQuestionType === "mcq" && (globalMcqOptions.some(o => !o.trim()) || globalMcqCorrectIndex < 0 || globalMcqCorrectIndex > 3))}
                  >
                    {t('jobDescription.addQuestion')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <AIQuestionGeneratorModal
        open={showAiGeneratorInCard}
        onClose={() => {
          setShowAiGeneratorInCard(false);
          setSelectedJobForAiGenerator(null);
        }}
        onAddQuestions={handleAiQuestionsAddedInCard}
        jobTitle={selectedJobForAiGenerator?.title}
        experienceLevel={selectedJobForAiGenerator?.experience_level}
        requiredSkills={selectedJobForAiGenerator?.required_skills}
        niceToHaveSkills={selectedJobForAiGenerator?.nice_to_have_skills}
        existingQuestions={selectedJobForAiGenerator?.questions}
        companyId={departments.find((dep) => dep.id === selectedJobForAiGenerator?.department || String(dep.id) === String(selectedJobForAiGenerator?.department))?.company}
      />

      <QuestionBankPickerModal
        open={showQuestionBankPicker}
        onClose={() => {
          setShowQuestionBankPicker(false);
          setSelectedJobForBank(null);
        }}
        onAddQuestions={handleQuestionBankAdded}
        existingQuestions={(selectedJobForBank || selectedJobForQuestion)?.questions}
      />

      <ShowDescriptionDialog open={openDescriptionDialog} onClose={() => { setOpenDescriptionDialog(false); setSelectedJobTitle(""); }} content={parse(description)} title={selectedJobTitle} />

      <JDFromAudioModal
        open={showAudioModal}
        onClose={handleCloseAudioModal}
        onApply={handleAudioJDApply}
        initialAuditId={notificationAuditId}
      />

      <JDDraftsModal
        open={showDraftsModal}
        onClose={() => setShowDraftsModal(false)}
        onSelectDraft={handleSelectDraft}
      />

      {/* 3-Dots Action Menu */}
      <Menu
        id="job-actions-menu"
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRTL ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRTL ? 'left' : 'right',
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              mt: 0.5,
              borderRadius: '12px',
              minWidth: '220px',
              border: '1px solid #f1f5f9',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
              py: 0.5,
              '& .MuiMenuItem-root': {
                px: 1.5,
                py: 1,
                fontSize: '0.8125rem',
                borderRadius: '8px',
                mx: 0.5,
                my: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#334155',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: '#f8fafc',
                  color: '#0f172a',
                },
              },
            },
          },
        }}
      >
        {/* 1. View Description */}
        <MenuItem onClick={handleMenuActionViewDescription}>
          <Eye size={15} className="text-blue-600 shrink-0" />
          <span className="font-medium">{t('jobDescription.viewDescription', 'View Description')}</span>
        </MenuItem>

        {/* 2. Matching Resumes */}
        <MenuItem onClick={handleMenuActionMatchingResumes}>
          <Users size={15} className="text-purple-600 shrink-0" />
          <span className="font-medium">{t('jobDescription.findMatchingResumes', 'Matching Resumes')}</span>
        </MenuItem>

        {/* 3. Edit */}
        <MenuItem onClick={handleMenuActionEdit}>
          <Edit size={15} className="text-amber-600 shrink-0" />
          <span className="font-medium">{t('jobDescription.edit', 'Edit')}</span>
        </MenuItem>

        {/* 4. View Timeline */}
        <MenuItem onClick={handleMenuActionTimeline}>
          <Clock size={15} className="text-indigo-600 shrink-0" />
          <span className="font-medium">{t('jobDescription.viewTimeline', 'View Timeline')}</span>
        </MenuItem>

        {/* 5. Interview Benchmarks */}
        <MenuItem onClick={handleMenuActionBenchmarks}>
          <ClipboardCheck size={15} className="text-sky-600 shrink-0" />
          <span className="font-medium">
            {selectedActionJob && expandedRows.has(selectedActionJob.id)
              ? t('jobDescription.hideBenchmarks', 'Hide Benchmarks')
              : t('jobDescription.interviewBenchmarks', 'Interview Benchmarks')}
          </span>
        </MenuItem>

        {/* 6. Add Questions */}
        <MenuItem onClick={handleMenuActionAddQuestion}>
          <Plus size={15} className="text-emerald-600 shrink-0" />
          <span className="font-medium">{t('jobDescription.buttons.addQuestions', 'Add Questions')}</span>
        </MenuItem>

        {/* Divider before archive */}
        <div className="my-1 border-t border-slate-100" />

        {/* 7. Archive / Unarchive */}
        {filters.status === "archived" ? (
          <MenuItem onClick={handleMenuActionArchive} sx={{ '&:hover': { backgroundColor: '#ecfdf5 !important', color: '#047857 !important' } }}>
            <ArchiveRestore size={15} className="text-emerald-600 shrink-0" />
            <span className="font-medium text-emerald-700">{t('jobDescription.unarchive', 'Unarchive')}</span>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleMenuActionArchive} sx={{ '&:hover': { backgroundColor: '#fef2f2 !important', color: '#b91c1c !important' } }}>
            <Trash2 size={15} className="text-red-500 shrink-0" />
            <span className="font-medium text-red-600">{t('jobDescription.archive', 'Archive')}</span>
          </MenuItem>
        )}
      </Menu>

      {/* Create / Add JD Dropdown Menu */}
      <Menu
        id="create-jd-menu"
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={handleCloseCreateMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isRTL ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: isRTL ? 'left' : 'right',
        }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              mt: 0.75,
              borderRadius: '12px',
              minWidth: '230px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
              py: 0.5,
              '& .MuiMenuItem-root': {
                px: 1.5,
                py: 1,
                fontSize: '0.8125rem',
                borderRadius: '8px',
                mx: 0.5,
                my: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#334155',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: '#f8fafc',
                  color: '#0f172a',
                },
              },
            },
          },
        }}
      >
        {/* 1. Add Job Description (Manual) */}
        <MenuItem onClick={handleMenuAddManual}>
          <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Plus size={15} className="stroke-[2.5]" />
          </div>
          <div className="flex flex-col text-left">
            <span className="font-semibold text-gray-800 text-xs">
              {t('jobDescription.addJobDescription', 'Add Job Description')}
            </span>
            <span className="text-[10px] text-gray-400">
              {t('jobDescription.createManually', 'Create manually with form')}
            </span>
          </div>
        </MenuItem>

        {/* 2. Upload JD */}
        <MenuItem onClick={handleMenuUploadJD} disabled={uploadingJD}>
          <div className="w-7 h-7 rounded-lg bg-green-50 text-green-600 flex items-center justify-center shrink-0">
            {uploadingJD ? <CircularProgress size={14} color="inherit" /> : <Upload size={15} />}
          </div>
          <div className="flex flex-col text-left">
            <span className="font-semibold text-gray-800 text-xs">
              {uploadingJD ? t('jobDescription.uploading', 'Uploading...') : t('jobDescription.uploadJD', 'Upload JD')}
            </span>
            <span className="text-[10px] text-gray-400">
              {t('jobDescription.uploadFileHint', 'Upload PDF, DOC, or DOCX')}
            </span>
          </div>
        </MenuItem>

        {/* 3. Audio JD */}
        <MenuItem onClick={handleMenuAudioJD}>
          <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <Mic size={15} />
          </div>
          <div className="flex flex-col text-left">
            <span className="font-semibold text-gray-800 text-xs">
              {t('jobDescription.audioJD', 'Audio JD')}
            </span>
            <span className="text-[10px] text-gray-400">
              {t('jobDescription.generateFromVoice', 'Generate from voice recording')}
            </span>
          </div>
        </MenuItem>

        {/* Divider */}
        <div className="my-1 border-t border-slate-100" />

        {/* 4. Saved Drafts */}
        <MenuItem onClick={handleMenuDrafts}>
          <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <Clock size={15} />
          </div>
          <div className="flex flex-col text-left">
            <span className="font-semibold text-gray-800 text-xs">
              {t('jobDescription.drafts', 'Drafts')}
            </span>
            <span className="text-[10px] text-gray-400">
              {t('jobDescription.viewSavedDrafts', 'View saved drafts')}
            </span>
          </div>
        </MenuItem>
      </Menu>

      {/* Timeline Drawer */}
      <Timeline
        open={timelineOpen}
        onClose={() => {
          setTimelineOpen(false);
          setSelectedJobIdForTimeline(null);
        }}
        apiEndpoint={selectedJobIdForTimeline
          ? `audit/timeline/job_descriptions/${selectedJobIdForTimeline}/`
          : "audit/timeline/job_descriptions/"}
        title={selectedJobIdForTimeline
          ? `${t('jobDescription.jobDescriptionsTimeline')} - ${jobDescriptions.find(j => j.id === selectedJobIdForTimeline)?.title || 'ID: ' + selectedJobIdForTimeline}`
          : t('jobDescription.jobDescriptionsTimeline')}
        pageName={t('jobDescription.title')}
        width={450}
      />

      {/* Fixed Vertical Timeline Button - Center Right/Left based on RTL */}
      <button
        onClick={() => setTimelineOpen(true)}
        className={`fixed ${isRTL ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 z-40 flex items-center justify-center text-white p-2 rounded-full transition-all duration-200 group hover:scale-110`}
        style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #ec4899 100%)',
          boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)'
        }}
      >
        <Clock size={14} />
        <span className={`absolute ${isRTL ? 'left-full ml-3' : 'right-full mr-3'} px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none`}>
          {t('jobDescription.timeline')}
        </span>
      </button>
    </PageShell>
  );
}
